import { forwardRef, useEffect, useRef, useState } from 'react';
import { AllieChatResponseProps } from './ChatResponse.types';
import { AllieText, DarkTooltip } from '../../../components';
import { AllieFactoryGptResponse } from '../FactoryGptResponse';
import {
  AllieStar06Icon,
  AllieCopyIcon,
  AllieDownloadIcon,
  AllieImageCopyIcon,
  AllieThumbsUpIcon,
  AllieThumbsDownIcon,
} from '../../../styling/icons';
import {
  ActionIconButton,
  AllieSaveOutlineIconStyled,
  AllieStarsAvatar,
  BubbleMessageFooter,
  BubbleMessageFooterRow,
  ChatResponseContainer,
  ContentNameMessage,
  HeaderContainer,
  MessageBubble,
  ThanksFeedbackContainer,
  TimeStamp,
  ToolbarActionContainer,
} from './ChatResponse.styles';
import { NegativeFeedback } from './components/NegativeFeedback/NegativeFeedback';
import { FactoryGptTextResponse } from '../FactoryGptTextResponse';
import { TypewriterText } from './components/TypewriterText';
import { MarkdownText } from '../MarkdownText';

export const AllieChatResponse = forwardRef<HTMLDivElement, AllieChatResponseProps>(
  (
    {
      name,
      message,
      createdAt,
      isFactoryGPT = true,
      type,
      showActions,
      ratingReasons,
      labels,
      showFeedback,
      isAnswerSatisfying,
      favoriteQuestionId,
      showAvatar = true,
      onChangeLike,
      onCopy,
      onDownload,
      onDownloadCsv,
      onDownloadXlsx,
      onCopyAsImage,
      onSave,
      disableAnimation = false,
      animateResponse = false,
      slideAnimation = false,
      maxDuration = 3000,
      maxSpeed = 100,
      onRenderComplete,
      citations,
      renderCitationPreview,
      renderCitationModal,
      style,
      isMobileApp = true,
    },
    ref
  ) => {
    const [feedback, setFeedback] = useState<boolean | undefined>(isAnswerSatisfying);
    const [hideOptions, setHideOptions] = useState<boolean>(true);
    const [selectedReason, setSelectedReason] = useState<number | undefined>(undefined);
    const [showInputFeedback, setShowInputFeedback] = useState<boolean>(false);
    const [clear, setClear] = useState<object>({});
    const [showMessagePostFeedback, setShowMessagePostFeedback] = useState<boolean>(false);
    const [isSaved, setIsSaved] = useState<boolean>(!!favoriteQuestionId);
    const responseContainerRef = useRef<HTMLDivElement>(null);
    const messageBubbleRef = useRef<HTMLDivElement>(null);

    const shouldAnimate = animateResponse;

    useEffect(() => {
      setIsSaved(favoriteQuestionId !== undefined);
    }, [favoriteQuestionId]);

    const handleChangeFeedback = (value?: boolean) => {
      onChangeLike &&
        (!!value ||
          (value === undefined && showMessagePostFeedback) ||
          (value === undefined && feedback) ||
          (value === undefined && !feedback && hideOptions)) &&
        onChangeLike(value);
      setFeedback(value);
      setHideOptions(false);
      setSelectedReason(undefined);
      setShowMessagePostFeedback(false);
    };

    const onCompleteFeedback = async (id: number, text?: string) => {
      const result = await ratingReasons?.onSelected(id, text);
      if (result) {
        setShowMessagePostFeedback(true);
      }
    };

    const handleSelectedReason = (id: number) => {
      setSelectedReason(id);
      const reason = ratingReasons?.options.find((option) => option.id === id);
      if (reason?.manualFeedbackAllowed) {
        setShowInputFeedback(true);
      } else {
        setShowInputFeedback(false);
        onCompleteFeedback(id);
      }
    };

    const handleFeedback = (value: string) => {
      if (selectedReason) {
        onCompleteFeedback(selectedReason, value);
        setClear({});
      }
    };

    const handleSave = (value: boolean) => {
      onSave && onSave(value);
    };

    const collectGroupText = (): string | null => {
      const bubbleEl = messageBubbleRef.current;
      if (!bubbleEl) return null;

      let container: HTMLElement | null = bubbleEl;
      while (container && !container.className?.includes?.('ChatResponseContainer')) {
        container = container.parentElement;
      }
      if (!container) return null;

      const findResp = (el: HTMLElement): HTMLElement | null => {
        if (el.className?.includes?.('ChatResponseContainer')) return el;
        return el.querySelector('[class*="ChatResponseContainer"]') as HTMLElement | null;
      };

      let walkRoot: HTMLElement = container;
      for (let i = 0; i < 3; i++) {
        const parent = walkRoot.parentElement;
        if (!parent) break;
        const hasSibling =
          (walkRoot.previousElementSibling &&
            findResp(walkRoot.previousElementSibling as HTMLElement)) ||
          (walkRoot.nextElementSibling &&
            findResp(walkRoot.nextElementSibling as HTMLElement));
        if (hasSibling) break;
        walkRoot = parent;
      }

      const group: HTMLElement[] = [];

      let prev = walkRoot.previousElementSibling as HTMLElement | null;
      while (prev) {
        const chatResp = findResp(prev);
        if (!chatResp) break;
        if (chatResp.querySelector('[class*="BubbleMessageFooter"]')) break;
        group.unshift(chatResp);
        prev = prev.previousElementSibling as HTMLElement | null;
      }

      group.push(container);

      if (group.length <= 1) return null;

      const texts = group
        .map((resp) => {
          const bubble = resp.querySelector('[class*="MessageBubble"]') as HTMLElement | null;
          return bubble?.innerText?.trim() || '';
        })
        .filter(Boolean);

      return texts.join('\n\n');
    };

    const handleCopy = async () => {
      try {
        const groupText = collectGroupText();
        const textToCopy = groupText || message.replace(/\*\*([^*]+)\*\*/g, '$1');
        await navigator.clipboard.writeText(textToCopy);
        onCopy?.(textToCopy);
      } catch (error) {
        const errorType =
          error instanceof DOMException && error.name === 'NotAllowedError'
            ? 'clipboard_permission_denied'
            : 'unknown';
        onCopy?.(message, errorType);
      }
    };

    const handleDownload = () => {
      onDownload?.(message, messageBubbleRef.current);
    };

    const handleCopyAsImage = () => {
      onCopyAsImage?.(messageBubbleRef.current);
    };

    const renderResponse = (stringifiedMessage: string) => {
      if (type && ['chart', 'table'].includes(type)) {
        const parsedMessage = JSON.parse(stringifiedMessage);

        return (
          <div ref={responseContainerRef} style={{ width: '100%', height: '100%' }}>
            <AllieFactoryGptResponse
              {...parsedMessage}
              unit={labels?.units?.[parsedMessage.unit] || parsedMessage.unit}
              trendlineLabel={labels?.trendline}
              containerRef={responseContainerRef}
              disableAnimation={disableAnimation}
              onDownloadCsv={onDownloadCsv}
              downloadCsvTooltip={labels?.downloadCsvTooltip}
              onDownloadXlsx={onDownloadXlsx}
            />
          </div>
        );
      }

      if (type === 'multimedia') {
        const parsedMessage = JSON.parse(stringifiedMessage);
        return (
          <div
            ref={responseContainerRef}
            style={{ width: '100%', height: '100%', margin: '8px 0' }}
          >
            {parsedMessage.title && (
              <AllieText type="h4" color="neutral-90">
                {parsedMessage.title}
              </AllieText>
            )}
            {parsedMessage.subtitle && (
              <AllieText type="body2" color="neutral-70">
                {parsedMessage.subtitle}
              </AllieText>
            )}
            {parsedMessage.data.filter(Boolean).map((item: any, index: number) => {
              const { type: itemType, value, data: itemData } = item;

              switch (itemType) {
                case 'text':
                case 'string':
                  return shouldAnimate ? (
                    <TypewriterText
                      key={index}
                      message={value || itemData}
                      textColor="neutral-70"
                      animateResponse={shouldAnimate}
                      maxDuration={maxDuration}
                      maxSpeed={maxSpeed}
                      onRenderComplete={onRenderComplete}
                    />
                  ) : (
                    <MarkdownText
                      key={index}
                      content={value || itemData}
                      onDownloadCsv={onDownloadCsv}
                      downloadCsvTooltip={labels?.downloadCsvTooltip}
                      onDownloadXlsx={onDownloadXlsx}
                    />
                  );
                case 'chart':
                case 'table':
                  const parsedData = JSON.parse(value || itemData);
                  return (
                    <AllieFactoryGptResponse
                      {...parsedData}
                      key={index}
                      unit={labels?.units?.[parsedData.unit] || parsedData.unit}
                      trendlineLabel={labels?.trendline}
                      containerRef={responseContainerRef}
                      disableAnimation={disableAnimation}
                      onDownloadCsv={onDownloadCsv}
                      downloadCsvTooltip={labels?.downloadCsvTooltip}
                      onDownloadXlsx={onDownloadXlsx}
                    />
                  );
                default:
                  return null;
              }
            })}
          </div>
        );
      }

      return (
        <FactoryGptTextResponse
          message={stringifiedMessage}
          textColor="neutral-90"
          animateResponse={shouldAnimate}
          maxDuration={maxDuration}
          maxSpeed={maxSpeed}
          onRenderComplete={onRenderComplete}
          citations={citations}
          citationLabels={labels?.citations}
          renderCitationPreview={renderCitationPreview}
          renderCitationModal={renderCitationModal}
          onDownloadCsv={onDownloadCsv}
          downloadCsvTooltip={labels?.downloadCsvTooltip}
          onDownloadXlsx={onDownloadXlsx}
        />
      );
    };

    const renderAvatar = () => {
      if (!showAvatar) return null;

      if (isFactoryGPT) {
        return (
          <AllieStarsAvatar>
            <AllieStar06Icon display="contents" />
          </AllieStarsAvatar>
        );
      }

      return null;
    };

    return (
      <ChatResponseContainer
        ref={ref}
        isFactoryGPT={isFactoryGPT}
        className={slideAnimation ? 'slide-up-animation' : ''}
        style={style}
      >
        <ContentNameMessage
          isFactoryGPT={isFactoryGPT}
          style={isMobileApp ? { overflow: 'clip' } : {}}
        >
          {(name || showAvatar) && (
            <HeaderContainer isFactoryGPT={isFactoryGPT}>
              {(!isMobileApp || isFactoryGPT) && (
              <AllieText type={isMobileApp ? 'body1' : 'body3'} color="neutral-80">
                {name}
              </AllieText>
            )}
              {renderAvatar()}
            </HeaderContainer>
          )}

          <MessageBubble
            ref={messageBubbleRef}
            isFactoryGPT={isFactoryGPT}
          >
            {renderResponse(message)}
          </MessageBubble>

          {(createdAt || (showActions && isFactoryGPT)) && (
            <BubbleMessageFooter isFactoryGPT={isFactoryGPT}>
              <BubbleMessageFooterRow isFactoryGPT={isFactoryGPT}>
                {showActions && isFactoryGPT && (
                  <ToolbarActionContainer>
                    {onCopy && (
                      <DarkTooltip text={labels?.copyTooltip || ''} side="top" alignment="start">
                        <ActionIconButton onClick={handleCopy}>
                          <AllieCopyIcon />
                        </ActionIconButton>
                      </DarkTooltip>
                    )}
                    {onCopyAsImage && (
                      <DarkTooltip
                        text={labels?.copyAsImageTooltip || ''}
                        side="top"
                        alignment="start"
                      >
                        <ActionIconButton onClick={handleCopyAsImage}>
                          <AllieImageCopyIcon />
                        </ActionIconButton>
                      </DarkTooltip>
                    )}
                    {onDownload && (
                      <DarkTooltip
                        text={labels?.downloadTooltip || ''}
                        side="top"
                        alignment="start"
                      >
                        <ActionIconButton onClick={handleDownload}>
                          <AllieDownloadIcon />
                        </ActionIconButton>
                      </DarkTooltip>
                    )}
                    {onSave && (
                      <AllieSaveOutlineIconStyled
                        onClick={() => handleSave(!isSaved)}
                        isSaved={isSaved}
                        fontSize={16}
                      />
                    )}
                    {showFeedback && (
                      <>
                        {feedback !== false && (
                          <DarkTooltip
                            text={labels?.thumbsUpTooltip || ''}
                            side="top"
                            alignment="start"
                          >
                            <ActionIconButton
                              onClick={() => handleChangeFeedback(
                                feedback === true ? undefined : true
                              )}
                              $filled={feedback === true}
                            >
                              <AllieThumbsUpIcon />
                            </ActionIconButton>
                          </DarkTooltip>
                        )}
                        {feedback !== true && (
                          <DarkTooltip
                            text={labels?.thumbsDownTooltip || ''}
                            side="top"
                            alignment="start"
                          >
                            <ActionIconButton
                              onClick={() => handleChangeFeedback(
                                feedback === false ? undefined : false
                              )}
                              $filled={feedback === false}
                            >
                              <AllieThumbsDownIcon />
                            </ActionIconButton>
                          </DarkTooltip>
                        )}
                        {showMessagePostFeedback && (
                          <ThanksFeedbackContainer>
                            <AllieText type="body1" color="neutral-70">
                              {labels?.thanksFeedback}
                            </AllieText>
                          </ThanksFeedbackContainer>
                        )}
                      </>
                    )}
                  </ToolbarActionContainer>
                )}

                {createdAt && (
                  <TimeStamp isFactoryGPT={isFactoryGPT}>
                    <AllieText type="body3" color="neutral-60">
                      {createdAt}
                    </AllieText>
                  </TimeStamp>
                )}
              </BubbleMessageFooterRow>

              {showActions &&
                isFactoryGPT &&
                feedback === false &&
                !hideOptions &&
                !showMessagePostFeedback && (
                  <NegativeFeedback
                    options={ratingReasons?.options || []}
                    setHideOptions={setHideOptions}
                    selectedReason={selectedReason}
                    handleSelectedReason={handleSelectedReason}
                    showInputFeedback={showInputFeedback}
                    handleFeedback={handleFeedback}
                    clear={clear}
                    labelNegativeOptions={labels?.negativeOptions || ''}
                    placeholderOtherOption={labels?.placeholderOtherOption}
                  />
                )}
            </BubbleMessageFooter>
          )}
        </ContentNameMessage>
      </ChatResponseContainer>
    );
  }
);

AllieChatResponse.displayName = 'AllieChatResponse';
